import { FormEvent, useEffect, useRef, useState } from 'react'
import * as S from './styles';
import { Button } from 'base/src/components/Button';
import { Forms, FormSubmit } from 'base/src/components/Form/index';
import { Modal, ModalContent } from 'base/src/components/Modal';
import { toast } from 'react-toastify';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { useCarteira } from 'domain/utils/context/carteiraBaseContext';
import { useTransferencia } from '../../hooks/useTransferencia';
import { CarteiraBaseResponse } from 'domain/types/_carteira/carteiraBase/carteiraBaseResponse';
import { CarteiraUsuarioResponse } from 'domain/types/_carteira/carteiraBase/carteiraUsuarioResponse';
import { PagamentoPixResponse } from 'domain/types/_carteira/premiacao/transferenciaPixTypes';

export interface TransferenciaModalProps {
  isOpen: boolean;
  // eslint-disable-next-line no-unused-vars
  onOpenChange: (open: boolean) => void;
  homeInfo: CarteiraBaseResponse;
  CarteiraUsuario: CarteiraUsuarioResponse[];
}

interface TransferenciaRequest {
  valor: number;
  chavePix: string;
}

function TransferenciaModal(props: TransferenciaModalProps) {
  const [request, setRequest] = useState<TransferenciaRequest>({ valor: 0, chavePix: '' });
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const { walletBalance } = useCarteira();
  const { transferirPix, getCarteiraBase } = useTransferencia();

  const taxaPix = props.homeInfo?.TaxaPagamento || 0;
  const valorComTaxa = Number(request.valor) + Number(taxaPix);
  const saldoFinal = (walletBalance || 0) - valorComTaxa;
  const podeTransferir = saldoFinal >= 0 && request.valor > 0 && request.chavePix.trim() !== '' && validatePixKey(request.chavePix);

  useEffect(() => {
    if (props.CarteiraUsuario.length > 0) {
      getCarteiraBase(props.CarteiraUsuario[0].CarteiraID);
    }
  }, [props.CarteiraUsuario, getCarteiraBase]);

  // Reset form when modal closes
  useEffect(() => {
    if (!props.isOpen) {
      setRequest({ valor: 0, chavePix: '' });
      setIsLoading(false);
    }
  }, [props.isOpen]);

  function handleInputChange(field: keyof TransferenciaRequest) {
    return (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = field === 'valor' ? Number(event.target.value) || 0 : event.target.value;
      setRequest(prev => ({ ...prev, [field]: value }));
    };
  }

  function validatePixKey(chavePix: string): boolean {
    if (!chavePix.trim()) return false;
    
    // Validação básica para diferentes tipos de chave PIX
    const cpfRegex = /^\d{11}$/;
    const cnpjRegex = /^\d{14}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+55\d{10,11}$/;
    const randomKeyRegex = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;

    const cleanKey = chavePix.replace(/[^\w@.-]/g, '');
    
    return cpfRegex.test(cleanKey) ||
           cnpjRegex.test(cleanKey) ||
           emailRegex.test(chavePix) ||
           phoneRegex.test(chavePix) ||
           randomKeyRegex.test(chavePix);
  }

  async function handleForm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    
    if (!podeTransferir) {
      toast("Verifique os dados da transferência", { type: "warning" });
      return;
    }

    if (!validatePixKey(request.chavePix)) {
      toast("Chave PIX inválida", { type: "error" });
      return;
    }

    if (request.valor <= 0) {
      toast("Valor deve ser maior que zero", { type: "warning" });
      return;
    }

    if (valorComTaxa > (walletBalance || 0)) {
      toast("Saldo insuficiente para realizar a transferência", { type: "error" });
      return;
    }

    setIsLoading(true);
    
    const carteiraId = props.CarteiraUsuario[0]?.CarteiraID;
    
    if (!carteiraId) {
      toast("Erro: Carteira não encontrada", { type: "error" });
      setIsLoading(false);
      return;
    }

    const transferenciaRequest = {
      Valor: request.valor,
      ChavePix: request.chavePix,
      Descricao: "Transferência PIX"
    };

    transferirPix(carteiraId, transferenciaRequest, (success: boolean, response?: PagamentoPixResponse) => {
      setIsLoading(false);
      
      if (success && response?.Success) {
        toast("Transferência realizada com sucesso!", { type: "success" });
        
        // Atualizar saldo
        if (props.CarteiraUsuario.length > 0) {
          getCarteiraBase(props.CarteiraUsuario[0].CarteiraID);
        }
        
        // Reset form and close modal
        setRequest({ valor: 0, chavePix: '' });
        props.onOpenChange(false);
        
      } else {
        const errorMessage = response?.Message || "Erro ao realizar transferência";
        toast(errorMessage, { type: "error" });
      }
    });
  }

  function handleOpenChange() {
    props.onOpenChange(false)
  }

  return (
    <Modal open={props.isOpen} onOpenChange={props.onOpenChange}>
      <ModalContent style={{ width: 600, maxWidth: '90vw' }}>
        <Forms formRef={formRef} handleForm={handleForm}>
          <S.Container>
            <S.Header>
              <S.Title>Transferência PIX</S.Title>
              <S.Subtitle>Transfira dinheiro usando chave PIX</S.Subtitle>
            </S.Header>
            
            <S.FormContainer>
              <S.InputGroup>
                <S.Label htmlFor="valor">Valor da Transferência</S.Label>
                <S.Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={request.valor || ''}
                  onChange={handleInputChange('valor')}
                  disabled={isLoading}
                />
              </S.InputGroup>

              <S.InputGroup>
                <S.Label htmlFor="chavePix">Chave PIX</S.Label>
                <S.Input
                  id="chavePix"
                  type="text"
                  placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
                  value={request.chavePix}
                  onChange={handleInputChange('chavePix')}
                  disabled={isLoading}
                />
                {request.chavePix && !validatePixKey(request.chavePix) && (
                  <S.ErrorMessage>Chave PIX inválida</S.ErrorMessage>
                )}
              </S.InputGroup>
            </S.FormContainer>

            <S.Overview red={!podeTransferir && request.valor > 0}>
              <S.InfoContainer>
                <S.InfoRow>
                  <S.InfoLabel>Saldo Atual:</S.InfoLabel>
                  <S.InfoValue positive>{formatToBRL(walletBalance || 0)}</S.InfoValue>
                </S.InfoRow>
                
                <S.InfoRow>
                  <S.InfoLabel>Valor da Transferência:</S.InfoLabel>
                  <S.InfoValue>{formatToBRL(request.valor)}</S.InfoValue>
                </S.InfoRow>
                
                <S.InfoRow>
                  <S.InfoLabel>Taxa PIX:</S.InfoLabel>
                  <S.InfoValue>{formatToBRL(taxaPix)}</S.InfoValue>
                </S.InfoRow>
                
                <S.InfoRow>
                  <S.InfoLabel>Total com Taxa:</S.InfoLabel>
                  <S.InfoValue>{formatToBRL(valorComTaxa)}</S.InfoValue>
                </S.InfoRow>
                
                <S.InfoRow>
                  <S.InfoLabel>Saldo Final:</S.InfoLabel>
                  <S.InfoValue negative={saldoFinal < 0} positive={saldoFinal >= 0}>
                    {formatToBRL(saldoFinal)}
                  </S.InfoValue>
                </S.InfoRow>

                <S.ButtonGroup>
                  <Button 
                    size='lg' 
                    variant='tertiary'
                    onClick={handleOpenChange}
                    disabled={isLoading}
                    type="button"
                  >
                    Cancelar
                  </Button>
                  <FormSubmit asChild>
                    <Button 
                      size='lg' 
                      disabled={!podeTransferir || isLoading}
                      variant='primary'
                      type="submit"
                    >
                      {isLoading ? 'Processando...' : 'Transferir'}
                    </Button>
                  </FormSubmit>
                </S.ButtonGroup>
              </S.InfoContainer>
            </S.Overview>
          </S.Container>
        </Forms>
      </ModalContent>
    </Modal>
  )
}

export default TransferenciaModal;
